/**
 * AccessibilitySettingsPanel — modal that lets a student toggle the 5
 * accessibility preferences. Always reachable via AccessibilityFloatingButton,
 * Esc closes the modal, and tabbing is trapped inside (browser default suffices
 * for our shallow content).
 */
import { useEffect, useRef } from "react";
import { Accessibility, X, Type, Contrast, ZapOff, Volume2, Mic, RotateCcw } from "lucide-react";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import {
  isSpeechRecognitionSupported,
} from "@/components/accessibility/MicButton";
import {
  isSpeechSynthesisSupported,
} from "@/components/accessibility/ReadAloudButton";

export default function AccessibilitySettingsPanel({ open, onClose }) {
  const { settings, update, reset } = useAccessibility();
  const closeRef = useRef(null);

  // Auto-focus close when opened; Esc closes.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const speechIn = isSpeechRecognitionSupported();
  const speechOut = isSpeechSynthesisSupported();

  const ToggleRow = ({ id, icon: Icon, title, description, checked, onChange, disabled, disabledReason }) => (
    <li className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div>
          <label htmlFor={id} className="font-semibold text-sm text-gray-900 cursor-pointer">
            {title}
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            {disabled && disabledReason ? disabledReason : description}
          </p>
        </div>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={id}
        className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? "bg-emerald-500" : "bg-gray-300"
        } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </li>
  );

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="a11y-settings-title"
      data-testid="a11y-settings-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-purple-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-fuchsia-50">
          <h2 id="a11y-settings-title" className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Accessibility className="w-5 h-5 text-purple-600" aria-hidden="true" />
            Accessibility
          </h2>
          <button
            ref={closeRef}
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-white/60"
            aria-label="Close accessibility settings"
            data-testid="a11y-settings-close"
          >
            <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 py-3">
          <p className="text-xs text-gray-500 mb-2">
            These choices are saved on this device. Voice features run in your browser — your voice is only used to turn speech into text.
          </p>
          <ul>
            <ToggleRow
              id="a11y-toggle-large-text"
              icon={Type}
              title="Larger text"
              description="Bumps body text and labels for easier reading."
              checked={settings.largeText}
              onChange={(v) => update({ largeText: v })}
            />
            <ToggleRow
              id="a11y-toggle-high-contrast"
              icon={Contrast}
              title="High contrast"
              description="Strong borders and bolder colors for low-vision users."
              checked={settings.highContrast}
              onChange={(v) => update({ highContrast: v })}
            />
            <ToggleRow
              id="a11y-toggle-reduce-motion"
              icon={ZapOff}
              title="Reduce motion"
              description="Pauses bouncing dots, confetti, and other animations."
              checked={settings.reduceMotion}
              onChange={(v) => update({ reduceMotion: v })}
            />
            <ToggleRow
              id="a11y-toggle-read-aloud"
              icon={Volume2}
              title="Read-aloud buttons"
              description="Show 'Read aloud' next to LemonBot replies, lessons, and feedback."
              checked={settings.readAloudEnabled}
              onChange={(v) => update({ readAloudEnabled: v })}
              disabled={!speechOut}
              disabledReason="This browser doesn't support text-to-speech."
            />
            <ToggleRow
              id="a11y-toggle-speech-input"
              icon={Mic}
              title="Voice typing"
              description="Show microphone buttons in chat, pitch, and reflections. Edit before submitting."
              checked={settings.speechInputEnabled}
              onChange={(v) => update({ speechInputEnabled: v })}
              disabled={!speechIn}
              disabledReason="This browser doesn't support speech-to-text."
            />
          </ul>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900"
            data-testid="a11y-settings-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800"
            data-testid="a11y-settings-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
